import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Input,
  InputNumber,
  message,
  Typography,
  Select,
  Checkbox,
  Card,
  Row,
  Col,
  Image
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { HomeOutlined } from '@ant-design/icons';
import '../pages/MemberManagement.css';
import { DEFAULT_CONSUMABLE_IMAGE, DEFAULT_REAGENT_IMAGE } from '../constants/defaultImages';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

const resolveUploadedImageUrl = (url?: string) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads/')) return `/api${url}`;
  return url;
};

type ConsumableItem = {
  name?: string;
  model?: string;
  brand?: string;
  unit?: string;
  purchaseChannel?: string;
  purchaseLink?: string;
  supplier?: string;
  amount?: string;
  price?: string;
  total?: string;
  image?: string;
};

type ReagentItem = {
  category?: string;
  name?: string;
  cas?: string;
  catalogNumber?: string;
  vendor?: string;
  specification?: string;
  unit?: string;
  purchaseChannel?: string;
  amount?: string;
  price?: string;
  total?: string;
  image?: string;
};

type OrderRow = {
  _id: string;
  category: string;
  applicant: string;
  teamId?: string;
  totalAmount?: number;
  approvedAt?: string;
  appliedAt?: string;
  itemSummary?: string;
  merchantOrder?: {
    orderStatus?: string;
    merchantName?: string;
    originalAmount?: number;
    quotedAmount?: number | null;
    pendingQuotedAmount?: number | null;
    lineQuotes?: Array<{
      projectIndex: number;
      quantity?: number;
      unitPrice?: number;
      lineAmount?: number;
    }>;
    merchantFeedback?: string;
    rejectReason?: string;
    platformOrderNote?: {
      completed?: boolean;
      price?: number | null;
      link?: string;
      submittedAt?: string | null;
      confirmedAt?: string | null;
      lockedAt?: string | null;
    };
    shippingAddress?: string;
    recipientName?: string;
    recipientPhone?: string;
    trackingNumbers?: Array<{ number: string; linkedAt?: string }>;
  };
  consumableProjects?: ConsumableItem[];
  reagentProjects?: ReagentItem[];
};

const STATUS_LABEL: Record<string, { color: string; text: string }> = {
  pending_accept: { color: 'orange', text: '待接单' },
  accepted: { color: 'blue', text: '已接单' },
  awaiting_shipment: { color: 'processing', text: '待发货' },
  price_pending: { color: 'gold', text: '改价审批中（历史）' },
  shipped: { color: 'green', text: '已发货' },
  completed: { color: 'default', text: '已完成' },
  rejected: { color: 'red', text: '已拒单' }
};

async function merchantFetch(path: string, options?: RequestInit) {
  const token = localStorage.getItem('token');
  const res = await fetch(`/api/merchant${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {})
    }
  });
  return res.json();
}

const MerchantOrders: React.FC = () => {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detail, setDetail] = useState<OrderRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [priceModal, setPriceModal] = useState<OrderRow | null>(null);
  const [shipModal, setShipModal] = useState<OrderRow | null>(null);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [lineQuoteAmounts, setLineQuoteAmounts] = useState<Array<number | null>>([]);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [note, setNote] = useState('');
  const [rejectModal, setRejectModal] = useState<OrderRow | null>(null);
  const [platformNoteModal, setPlatformNoteModal] = useState<OrderRow | null>(null);
  const [platformOrderPrice, setPlatformOrderPrice] = useState<number | null>(null);
  const [platformOrderLink, setPlatformOrderLink] = useState('');

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
      const body = await merchantFetch(`/orders${q}`);
      if (!body.success) {
        message.error(body.error || '加载失败');
        setOrders([]);
        return;
      }
      setOrders(body.data || []);
    } catch {
      message.error('网络异常');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const teamGroups = useMemo(() => {
    const map = new Map<string, OrderRow[]>();
    for (const o of orders) {
      const tid = String(o.teamId || '');
      if (!map.has(tid)) map.set(tid, []);
      map.get(tid)!.push(o);
    }
    return map;
  }, [orders]);

  const accept = async (row: OrderRow) => {
    const body = await merchantFetch(`/orders/${row._id}/accept`, {
      method: 'POST',
      body: JSON.stringify({ feedback: '已接单' })
    });
    if (!body.success) {
      message.error(body.error || '接单失败');
      return;
    }
    message.success('已接单');
    void loadOrders();
  };

  const reject = async () => {
    if (!rejectModal) return;
    const body = await merchantFetch(`/orders/${rejectModal._id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: note || '商家拒单' })
    });
    if (!body.success) {
      message.error(body.error || '拒单失败');
      return;
    }
    message.success('已拒单，团队申请已驳回');
    setRejectModal(null);
    setNote('');
    void loadOrders();
  };

  const getOrderProjects = (row: OrderRow | null | undefined) => {
    if (!row) return [] as Array<ConsumableItem | ReagentItem>;
    return row.category === '试剂申请' ? row.reagentProjects || [] : row.consumableProjects || [];
  };

  const buildInitialLineQuotes = (row: OrderRow) => {
    const projects = getOrderProjects(row);
    const existing = row.merchantOrder?.lineQuotes || [];
    const fallbackTotal =
      row.merchantOrder?.quotedAmount != null
        ? Number(row.merchantOrder.quotedAmount)
        : Number(row.totalAmount || 0);
    return projects.map((project, index) => {
      const fromQuote = existing.find((q) => Number(q.projectIndex) === index);
      if (fromQuote && fromQuote.lineAmount != null && Number.isFinite(Number(fromQuote.lineAmount))) {
        return Number(fromQuote.lineAmount);
      }
      const total = Number(project.total);
      if (Number.isFinite(total) && total >= 0 && String(project.total || '').trim() !== '') {
        return total;
      }
      const qty = Number(project.amount) || 0;
      const unit = Number(project.price) || 0;
      if (qty > 0 && unit >= 0 && String(project.price || '').trim() !== '') {
        return Math.round(qty * unit * 100) / 100;
      }
      if (projects.length === 1) return fallbackTotal;
      return null;
    });
  };

  const priceModalTotal = useMemo(() => {
    return lineQuoteAmounts.reduce<number>((sum, value) => sum + (Number(value) || 0), 0);
  }, [lineQuoteAmounts]);

  const proposePrice = async () => {
    if (!priceModal) return;
    const projects = getOrderProjects(priceModal);
    if (!projects.length) {
      message.warning('订单没有商品明细，无法改价');
      return;
    }
    if (lineQuoteAmounts.length !== projects.length || lineQuoteAmounts.some((v) => v == null || !Number.isFinite(Number(v)) || Number(v) < 0)) {
      message.warning('请为每一项商品填写有效的行总价');
      return;
    }
    const lineQuotes = projects.map((_, projectIndex) => ({
      projectIndex,
      lineAmount: Number(lineQuoteAmounts[projectIndex])
    }));
    const amountText = priceModalTotal.toFixed(2);
    const detailLines = projects.map((project, index) => {
      const label =
        priceModal.category === '试剂申请'
          ? (project as ReagentItem).name || (project as ReagentItem).cas || `试剂#${index + 1}`
          : (project as ConsumableItem).name || (project as ConsumableItem).model || `耗材#${index + 1}`;
      return `${label}：¥${Number(lineQuoteAmounts[index]).toFixed(2)}`;
    });
    Modal.confirm({
      title: '确认按商品行调整实付价格？',
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            订单实付合计将调整为 <strong>¥{amountText}</strong>。
          </p>
          <div style={{ marginBottom: 8, whiteSpace: 'pre-wrap' }}>{detailLines.join('\n')}</div>
          <p style={{ marginBottom: 0 }}>
            改价将直接生效，无需团队再次审批；系统会通知购买人与对应耗材/试剂审批人。
          </p>
        </div>
      ),
      okText: '确认改价',
      cancelText: '取消',
      onOk: async () => {
        const body = await merchantFetch(`/orders/${priceModal._id}/propose-price`, {
          method: 'POST',
          body: JSON.stringify({ lineQuotes, quotedAmount: priceModalTotal, note })
        });
        if (!body.success) {
          message.error(body.error || '改价失败');
          return Promise.reject(new Error(body.error || '改价失败'));
        }
        message.success('实付价格已按商品行更新，已通知购买人与审批人');
        setPriceModal(null);
        setLineQuoteAmounts([]);
        setNote('');
        void loadOrders();
      }
    });
  };

  const ship = async () => {
    if (!shipModal || !trackingNumber.trim()) {
      message.warning('请填写快递单号');
      return;
    }
    const body = await merchantFetch(`/orders/${shipModal._id}/ship`, {
      method: 'POST',
      body: JSON.stringify({ trackingNumber, note })
    });
    if (!body.success) {
      message.error(body.error || '发货失败');
      return;
    }
    message.success('已发货');
    setShipModal(null);
    setTrackingNumber('');
    setNote('');
    void loadOrders();
  };

  const mergeShip = async () => {
    if (selectedIds.length < 1 || !trackingNumber.trim()) {
      message.warning('请选择订单并填写快递单号');
      return;
    }
    const selected = orders.filter((o) => selectedIds.includes(o._id));
    const teams = new Set(selected.map((o) => String(o.teamId || '')));
    if (teams.size !== 1) {
      message.error('仅可合并同一团队的订单');
      return;
    }
    const body = await merchantFetch('/orders/merge-ship', {
      method: 'POST',
      body: JSON.stringify({ orderIds: selectedIds, trackingNumber, note })
    });
    if (!body.success) {
      message.error(body.error || '合并发货失败');
      return;
    }
    message.success(`已关联快递单号到 ${body.data?.count || selectedIds.length} 笔订单`);
    setMergeModalOpen(false);
    setSelectedIds([]);
    setTrackingNumber('');
    setNote('');
    void loadOrders();
  };

  const savePlatformOrderNote = async () => {
    if (!platformNoteModal) return;
    if (platformOrderPrice == null || platformOrderPrice < 0) {
      message.warning('请填写有效的备注价格');
      return;
    }
    if (!/^https?:\/\/\S+$/i.test(platformOrderLink.trim())) {
      message.warning('请填写以 http:// 或 https:// 开头的下单链接');
      return;
    }
    const body = await merchantFetch(`/orders/${platformNoteModal._id}/platform-order-note`, {
      method: 'POST',
      body: JSON.stringify({
        price: platformOrderPrice,
        link: platformOrderLink.trim()
      })
    });
    if (!body.success) {
      message.error(body.error || '保存下单备注失败');
      return;
    }
    message.success('下单备注已提交，等待团队管理员确认');
    setPlatformNoteModal(null);
    if (detail?._id === platformNoteModal._id) setDetail(body.data);
    void loadOrders();
  };

  const openDetail = async (row: OrderRow) => {
    setDetail(row);
    setDetailLoading(true);
    try {
      const body = await merchantFetch(`/orders/${row._id}`);
      if (body.success && body.data) {
        setDetail(body.data);
      }
    } catch {
      /* 列表数据已可展示 */
    } finally {
      setDetailLoading(false);
    }
  };

  const columns: ColumnsType<OrderRow> = [
    {
      title: '',
      width: 48,
      render: (_, row) => (
        <Checkbox
          checked={selectedIds.includes(row._id)}
          disabled={!['awaiting_shipment', 'shipped', 'accepted'].includes(row.merchantOrder?.orderStatus || '')}
          onChange={(e) => {
            setSelectedIds((prev) =>
              e.target.checked ? [...prev, row._id] : prev.filter((id) => id !== row._id)
            );
          }}
        />
      )
    },
    {
      title: '订单',
      key: 'itemSummary',
      render: (_, row) => (
        <div>
          <div>{row.itemSummary || row.category}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.category} · {row.applicant}
          </Text>
        </div>
      )
    },
    {
      title: '状态',
      width: 120,
      render: (_, row) => {
        const s = row.merchantOrder?.orderStatus || '';
        const meta = STATUS_LABEL[s] || { color: 'default', text: s || '-' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '金额',
      width: 140,
      render: (_, row) => {
        const mo = row.merchantOrder;
        const orig = mo?.originalAmount ?? row.totalAmount ?? 0;
        const quoted = mo?.quotedAmount;
        const pending = mo?.pendingQuotedAmount;
        return (
          <div>
            <div>申请 ¥{Number(orig).toFixed(2)}</div>
            {quoted != null && <div>实付 ¥{Number(quoted).toFixed(2)}</div>}
            {pending != null && mo?.orderStatus === 'price_pending' && (
              <Text type="warning">待确认 ¥{Number(pending).toFixed(2)}</Text>
            )}
          </div>
        );
      }
    },
    {
      title: '快递',
      width: 140,
      render: (_, row) => {
        const list = row.merchantOrder?.trackingNumbers || [];
        if (!list.length) return '—';
        return list.map((t) => t.number).join('、');
      }
    },
    {
      title: '平台下单',
      width: 150,
      render: (_, row) => {
        const platformNote = row.merchantOrder?.platformOrderNote;
        if (platformNote?.completed) {
          return (
            <div>
              <Tag color="green">是 · 已锁定</Tag>
              {platformNote.submittedAt ? (
                <div>¥{Number(platformNote.price || 0).toFixed(2)}</div>
              ) : null}
            </div>
          );
        }
        if (!platformNote?.submittedAt) return <Tag>未提交备注</Tag>;
        return (
          <div>
            <Tag color="orange">待团队确认</Tag>
            <div>¥{Number(platformNote.price || 0).toFixed(2)}</div>
          </div>
        );
      }
    },
    {
      title: '操作',
      width: 280,
      render: (_, row) => {
        const st = row.merchantOrder?.orderStatus;
        return (
          <Space wrap size="small">
            <Button size="small" onClick={() => void openDetail(row)}>
              详情
            </Button>
            {['accepted', 'awaiting_shipment', 'shipped', 'completed'].includes(st || '') &&
              !row.merchantOrder?.platformOrderNote?.completed && (
                <Button
                  size="small"
                  onClick={() => {
                    setPlatformNoteModal(row);
                    setPlatformOrderPrice(row.merchantOrder?.platformOrderNote?.price ?? null);
                    setPlatformOrderLink(row.merchantOrder?.platformOrderNote?.link || '');
                  }}
                >
                  {row.merchantOrder?.platformOrderNote?.submittedAt ? '修改备注' : '填写备注'}
                </Button>
              )}
            {st === 'pending_accept' && (
              <>
                <Button size="small" type="primary" onClick={() => void accept(row)}>
                  接单
                </Button>
                <Button size="small" danger onClick={() => { setRejectModal(row); setNote(''); }}>
                  拒单
                </Button>
              </>
            )}
            {['accepted', 'awaiting_shipment', 'price_pending'].includes(st || '') && (
              <Button
                size="small"
                onClick={() => {
                  setPriceModal(row);
                  setLineQuoteAmounts(buildInitialLineQuotes(row));
                  setNote('');
                }}
              >
                改价
              </Button>
            )}
            {['awaiting_shipment', 'accepted', 'shipped'].includes(st || '') && (
              <Button
                size="small"
                type="primary"
                ghost
                onClick={() => {
                  setShipModal(row);
                  setTrackingNumber('');
                  setNote('');
                }}
              >
                {st === 'shipped' ? '补关联单号' : '发货'}
              </Button>
            )}
            {['pending_accept', 'accepted', 'awaiting_shipment', 'price_pending'].includes(st || '') &&
              st !== 'pending_accept' && (
                <Button size="small" danger onClick={() => { setRejectModal(row); setNote(''); }}>
                  拒单
                </Button>
              )}
          </Space>
        );
      }
    }
  ];

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>订单管理</span>
      </nav>

      <header className="mm-page-head d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="mm-title">订单管理</h1>
          <p className="mm-subtitle mb-0">
            仅展示团队内部审批通过后的商家申请订单；可接单、改价、发货及同团队合并快递单号。
          </p>
        </div>
        <Space wrap>
          <Select
            allowClear
            placeholder="按状态筛选"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.entries(STATUS_LABEL).map(([value, v]) => ({ value, label: v.text }))}
          />
          <Button
            type="primary"
            disabled={selectedIds.length < 1}
            onClick={() => {
              setTrackingNumber('');
              setNote('');
              setMergeModalOpen(true);
            }}
          >
            合并发货 / 补关联（{selectedIds.length}）
          </Button>
          <Button onClick={() => void loadOrders()}>刷新</Button>
        </Space>
      </header>

      <div className="content-area px-1 px-md-2">
        <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3">
          <Table
            rowKey="_id"
            loading={loading}
            columns={columns}
            dataSource={orders}
            pagination={{ pageSize: 10 }}
          />
        </div>
      </div>

      <Modal
        open={!!detail}
        title="订单详情"
        onCancel={() => setDetail(null)}
        footer={<Button onClick={() => setDetail(null)}>关闭</Button>}
        width={780}
      >
        {detail && (
          <div style={{ opacity: detailLoading ? 0.65 : 1 }}>
            <Paragraph className="mb-1">
              类型：{detail.category}
              <Text type="secondary" style={{ marginLeft: 12 }}>
                联系人：{detail.applicant}
              </Text>
            </Paragraph>
            <Paragraph className="mb-1">
              状态：
              {STATUS_LABEL[detail.merchantOrder?.orderStatus || '']?.text ||
                detail.merchantOrder?.orderStatus ||
                '—'}
            </Paragraph>
            <Paragraph className="mb-3">
              申请金额：¥
              {Number(detail.merchantOrder?.originalAmount ?? detail.totalAmount ?? 0).toFixed(2)}
              {detail.merchantOrder?.quotedAmount != null && (
                <span style={{ marginLeft: 12 }}>
                  实付金额：¥{Number(detail.merchantOrder.quotedAmount).toFixed(2)}
                </span>
              )}
            </Paragraph>
            {detail.merchantOrder?.rejectReason && (
              <Paragraph type="danger">拒单原因：{detail.merchantOrder.rejectReason}</Paragraph>
            )}
            {(detail.merchantOrder?.trackingNumbers || []).length > 0 && (
              <Paragraph>
                快递单号：
                {(detail.merchantOrder?.trackingNumbers || []).map((t) => t.number).join('、')}
              </Paragraph>
            )}
            <Card size="small" className="mb-3" title="平台下单备注" styles={{ body: { padding: 16 } }}>
              <Row gutter={[12, 8]}>
                <Col xs={24} sm={8}>
                  <Text type="secondary">是否完成平台下单</Text>
                  <div>
                    {detail.merchantOrder?.platformOrderNote?.completed
                      ? '是（团队已勾选，已锁定）'
                      : detail.merchantOrder?.platformOrderNote?.submittedAt
                        ? '否（待团队确认）'
                        : '否'}
                  </div>
                </Col>
                <Col xs={24} sm={8}>
                  <Text type="secondary">备注价格</Text>
                  <div>
                    {detail.merchantOrder?.platformOrderNote?.submittedAt
                      ? `¥${Number(detail.merchantOrder.platformOrderNote.price || 0).toFixed(2)}`
                      : ''}
                  </div>
                </Col>
                <Col xs={24}>
                  <Text type="secondary">下单链接</Text>
                  <div>
                    {detail.merchantOrder?.platformOrderNote?.submittedAt &&
                    detail.merchantOrder.platformOrderNote.link ? (
                      <Button
                        type="link"
                        size="small"
                        style={{ paddingInline: 0 }}
                        onClick={() =>
                          Modal.info({
                            title: '平台下单链接',
                            content: (
                              <div style={{ wordBreak: 'break-all' }}>
                                {detail.merchantOrder?.platformOrderNote?.link}
                              </div>
                            )
                          })
                        }
                      >
                        查看链接
                      </Button>
                    ) : null}
                  </div>
                </Col>
              </Row>
            </Card>

            <Card size="small" className="mb-3" title="收货信息" styles={{ body: { padding: 16 } }}>
              <Row gutter={[12, 8]}>
                <Col xs={12} sm={8}>
                  <Text type="secondary">收件人</Text>
                  <div>{detail.merchantOrder?.recipientName || '—'}</div>
                </Col>
                <Col xs={12} sm={8}>
                  <Text type="secondary">手机号</Text>
                  <div>{detail.merchantOrder?.recipientPhone || '—'}</div>
                </Col>
                <Col xs={24} sm={24}>
                  <Text type="secondary">收货地址</Text>
                  <div>{detail.merchantOrder?.shippingAddress || '—'}</div>
                </Col>
              </Row>
            </Card>

            {detail.category === '耗材申请' && (
              <>
                <Title level={5} style={{ marginBottom: 12 }}>
                  耗材明细
                </Title>
                {(detail.consumableProjects || []).length === 0 ? (
                  <Text type="secondary">暂无耗材明细</Text>
                ) : (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {(detail.consumableProjects || []).map((p, idx) => (
                      <Card
                        key={`c-${idx}`}
                        size="small"
                        title={`耗材 ${idx + 1}`}
                        styles={{ body: { padding: 16 } }}
                      >
                        <Row gutter={[16, 12]} align="top">
                          <Col flex="112px">
                            <Image
                              src={resolveUploadedImageUrl(p.image) || DEFAULT_CONSUMABLE_IMAGE}
                              alt={p.name || '耗材'}
                              preview={{ mask: '点击放大' }}
                              style={{
                                width: 96,
                                height: 96,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #eee'
                              }}
                            />
                          </Col>
                          <Col flex="auto">
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>{p.name || '—'}</div>
                            <Row gutter={[12, 8]}>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">型号</Text>
                                <div>{p.model || '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">品牌</Text>
                                <div>{p.brand || ''}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">数量</Text>
                                <div>
                                  {p.amount || '—'} {p.unit || ''}
                                </div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">单价</Text>
                                <div>{p.price ? `¥${p.price}` : '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">小计</Text>
                                <div>{p.total ? `¥${p.total}` : '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">采购</Text>
                                <div>
                                  {p.purchaseChannel === '网购' && p.purchaseLink ? (
                                    <div style={{ wordBreak: 'break-all' }}>{p.purchaseLink}</div>
                                  ) : p.purchaseChannel === '其他' ? (
                                    p.supplier || '其他'
                                  ) : (
                                    p.purchaseChannel || '—'
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </Space>
                )}
              </>
            )}

            {detail.category === '试剂申请' && (
              <>
                <Title level={5} style={{ marginBottom: 12 }}>
                  试剂明细
                </Title>
                {(detail.reagentProjects || []).length === 0 ? (
                  <Text type="secondary">暂无试剂明细</Text>
                ) : (
                  <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {(detail.reagentProjects || []).map((p, idx) => (
                      <Card
                        key={`r-${idx}`}
                        size="small"
                        title={`试剂 ${idx + 1}`}
                        styles={{ body: { padding: 16 } }}
                      >
                        <Row gutter={[16, 12]} align="top">
                          <Col flex="112px">
                            <Image
                              src={resolveUploadedImageUrl(p.image) || DEFAULT_REAGENT_IMAGE}
                              alt={p.name || p.cas || '试剂'}
                              preview={{ mask: '点击放大' }}
                              style={{
                                width: 96,
                                height: 96,
                                objectFit: 'cover',
                                borderRadius: 8,
                                border: '1px solid #eee'
                              }}
                            />
                          </Col>
                          <Col flex="auto">
                            <div style={{ fontWeight: 600, marginBottom: 8 }}>
                              {p.name || p.cas || '—'}
                            </div>
                            <Row gutter={[12, 8]}>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">CAS</Text>
                                <div>{p.cas || '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">货号</Text>
                                <div>{p.catalogNumber || ''}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">品牌</Text>
                                <div>{p.vendor || '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">规格</Text>
                                <div>{p.specification || '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">数量</Text>
                                <div>
                                  {p.amount || '—'} {p.unit || ''}
                                </div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">单价</Text>
                                <div>{p.price ? `¥${p.price}` : '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">小计</Text>
                                <div>{p.total ? `¥${p.total}` : '—'}</div>
                              </Col>
                              <Col xs={12} sm={8}>
                                <Text type="secondary">类别 / 渠道</Text>
                                <div>
                                  {[p.category, p.purchaseChannel].filter(Boolean).join(' · ') || '—'}
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </Space>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={!!platformNoteModal}
        title="平台下单备注"
        onCancel={() => setPlatformNoteModal(null)}
        onOk={() => void savePlatformOrderNote()}
        okText="提交备注"
      >
        <Paragraph type="secondary">
          提交后由团队耗材/试剂管理员确认是否完成平台下单；确认前可修改，确认后将永久锁定。
        </Paragraph>
        <div style={{ marginBottom: 12 }}>
          <Text>备注价格</Text>
          <InputNumber
            min={0}
            precision={2}
            value={platformOrderPrice}
            onChange={setPlatformOrderPrice}
            placeholder="请输入备注价格"
            style={{ width: '100%', marginTop: 8 }}
          />
        </div>
        <div>
          <Text>下单链接</Text>
          <Input
            value={platformOrderLink}
            onChange={(event) => setPlatformOrderLink(event.target.value)}
            placeholder="https://"
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>

      <Modal
        open={!!priceModal}
        title="按商品行调整实付价格"
        onCancel={() => {
          setPriceModal(null);
          setLineQuoteAmounts([]);
        }}
        onOk={() => void proposePrice()}
        okText="提交改价"
        width={640}
      >
        <Paragraph type="secondary">
          请分别填写每一项商品的行总价；订单实付金额为各行之和。改价直接生效，无需团队再次审批。
        </Paragraph>
        <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 12 }}>
          {getOrderProjects(priceModal).map((project, index) => {
            const label =
              priceModal?.category === '试剂申请'
                ? (project as ReagentItem).name || (project as ReagentItem).cas || `试剂#${index + 1}`
                : (project as ConsumableItem).name ||
                  (project as ConsumableItem).model ||
                  `耗材#${index + 1}`;
            const qty = Number(project.amount) || 0;
            const unitHint =
              lineQuoteAmounts[index] != null && qty > 0
                ? `折合单价 ¥${(Number(lineQuoteAmounts[index]) / qty).toFixed(2)}`
                : qty > 0
                  ? `数量 ${qty}`
                  : '';
            return (
              <div key={`quote-${index}`}>
                <Text>
                  {label}
                  {qty > 0 ? ` × ${qty}` : ''}
                </Text>
                {unitHint ? (
                  <div>
                    <Text type="secondary">{unitHint}</Text>
                  </div>
                ) : null}
                <InputNumber
                  style={{ width: '100%', marginTop: 8 }}
                  min={0}
                  precision={2}
                  value={lineQuoteAmounts[index]}
                  placeholder="行总价（元）"
                  onChange={(value) => {
                    setLineQuoteAmounts((prev) => {
                      const next = [...prev];
                      next[index] = value;
                      return next;
                    });
                  }}
                />
              </div>
            );
          })}
        </Space>
        <div style={{ marginBottom: 12 }}>
          <Text strong>订单实付合计：¥{priceModalTotal.toFixed(2)}</Text>
        </div>
        <TextArea rows={3} placeholder="备注（可选）" value={note} onChange={(e) => setNote(e.target.value)} />
      </Modal>

      <Modal
        open={!!shipModal}
        title={shipModal?.merchantOrder?.orderStatus === 'shipped' ? '补关联快递单号' : '发货'}
        onCancel={() => setShipModal(null)}
        onOk={() => void ship()}
        okText="确认"
      >
        <Input
          placeholder="快递单号"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <TextArea rows={2} placeholder="备注（可选）" value={note} onChange={(e) => setNote(e.target.value)} />
      </Modal>

      <Modal
        open={mergeModalOpen}
        title="合并发货 / 补关联快递单号"
        onCancel={() => setMergeModalOpen(false)}
        onOk={() => void mergeShip()}
        okText="确认关联"
      >
        <Paragraph>
          已选 {selectedIds.length} 笔订单。须为同一团队；待发货可合并，已发货可补关联。
        </Paragraph>
        <Input
          placeholder="共用快递单号"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <TextArea rows={2} placeholder="备注（可选）" value={note} onChange={(e) => setNote(e.target.value)} />
      </Modal>

      <Modal
        open={!!rejectModal}
        title="拒单"
        onCancel={() => setRejectModal(null)}
        onOk={() => void reject()}
        okText="确认拒单"
        okButtonProps={{ danger: true }}
      >
        <Paragraph type="danger">拒单后，对应团队申请将自动驳回。</Paragraph>
        <TextArea rows={3} placeholder="拒单原因" value={note} onChange={(e) => setNote(e.target.value)} />
      </Modal>
    </div>
  );
};

export default MerchantOrders;
